import React from 'react';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  RobotOutlined,
  SoundOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { voiceTurnErrorMessage } from './voiceLiveEvents';

const QUESTION_MAX = 120;

const truncate = (text, max = QUESTION_MAX) => {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trim()}…`;
};

/**
 * Thin live status strip for the current voice turn.
 * Full answers belong in the chat history above — this only shows the question
 * (truncated) plus a short status line.
 */
export default function VoiceLiveTranscript({ live, active, muted = false, autoMuted = false, isLight, textPrimary, textSecondary, borderColor }) {
  if (!active || !live) return null;

  const assistantText = live.assistantSentences.join(' ').trim();
  const stopped = live.command === 'stop';
  const speaking = Boolean(assistantText) && !stopped && !live.paused && !live.errorCode;
  const empty = !live.userText && !assistantText && !live.step && !live.errorCode && !live.reply && !live.paused && !stopped && !live.ignored;
  const question = truncate(live.userText);

  let statusIcon = null;
  let statusLabel = '';
  let statusText = '';
  let statusTestId = null;

  if (muted) {
    statusIcon = <UserOutlined aria-hidden />;
    statusLabel = 'Muted';
    statusText = 'Unmute to speak.';
    statusTestId = 'voice-muted';
  } else if (autoMuted) {
    statusIcon = <LoadingOutlined spin aria-hidden />;
    statusLabel = 'Sent';
    statusText = 'Mic off until the answer starts.';
    statusTestId = 'voice-auto-muted';
  } else if (live.answerCheck === 'found') {
    statusIcon = <CheckCircleOutlined aria-hidden />;
    statusLabel = 'Answer';
    statusText = 'Full answer is in the chat above.';
    statusTestId = 'voice-late-answer';
  } else if (live.errorCode) {
    statusIcon = live.answerCheck === 'pending' ? <LoadingOutlined spin aria-hidden /> : <ExclamationCircleOutlined aria-hidden />;
    statusLabel = 'Issue';
    statusText = `${voiceTurnErrorMessage(live.errorCode)}${live.answerCheck === 'pending' ? ' Waiting for chat…' : ''}`;
  } else if (stopped) {
    statusIcon = <StopOutlined aria-hidden />;
    statusLabel = 'Stopped';
    statusText = 'Answer discarded. Ask another question when ready.';
    statusTestId = 'voice-stopped';
  } else if (live.paused) {
    statusIcon = <PauseCircleOutlined aria-hidden />;
    statusLabel = 'Paused';
    statusText = 'Say “continue” or “stop”.';
    statusTestId = 'voice-paused';
  } else if (live.ignored) {
    statusIcon = <InfoCircleOutlined aria-hidden />;
    statusLabel = 'Not sent';
    statusText = live.ignored.reason === 'barge_in_disabled'
      ? 'Wait for the answer, or say “stop” first.'
      : 'Too short — say “continue” if the answer stopped.';
    statusTestId = 'voice-ignored';
  } else if (live.reply) {
    statusIcon = <RobotOutlined aria-hidden />;
    statusLabel = 'Assistant';
    statusText = truncate(live.reply, 100);
    statusTestId = 'voice-reply';
  } else if (speaking) {
    statusIcon = <SoundOutlined aria-hidden />;
    statusLabel = 'Speaking';
    statusText = 'Answer is appearing in the chat above.';
    statusTestId = 'voice-speaking';
  } else if (live.step) {
    statusIcon = <LoadingOutlined spin aria-hidden />;
    statusLabel = 'Working';
    statusText = live.step;
  } else if (empty) {
    statusIcon = null;
    statusLabel = '';
    statusText = 'Listening — speak your question.';
  } else if (live.userText && !live.userFinal) {
    statusIcon = <UserOutlined aria-hidden />;
    statusLabel = 'Hearing';
    statusText = question;
  } else if (live.userText) {
    statusIcon = <LoadingOutlined spin aria-hidden />;
    statusLabel = 'Working';
    statusText = 'Preparing an answer…';
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Live voice status"
      data-testid="voice-live-transcript"
      style={{
        margin: '0 0 8px',
        padding: '6px 10px',
        borderRadius: 8,
        border: `1px solid ${borderColor || (isLight ? '#E5E7EB' : 'rgba(255,255,255,0.12)')}`,
        background: isLight ? 'rgba(246, 46, 151, 0.04)' : 'rgba(246, 46, 151, 0.08)',
        color: textPrimary,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        opacity: live.done ? 0.8 : 1,
        fontSize: 12,
        lineHeight: 1.4,
        minHeight: 0,
      }}
    >
      {question && statusLabel !== 'Hearing' ? (
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'baseline',
            color: textSecondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={live.userText}
        >
          <span style={{ fontWeight: 600, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <UserOutlined aria-hidden /> You
          </span>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontStyle: live.userFinal ? 'normal' : 'italic',
              color: textPrimary,
            }}
          >
            {question}
          </span>
        </div>
      ) : null}
      <div
        style={{ display: 'flex', gap: 6, alignItems: 'flex-start', color: textSecondary }}
        data-testid={statusTestId || undefined}
      >
        {statusLabel ? (
          <span style={{ fontWeight: 600, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, color: textPrimary }}>
            {statusIcon}
            {statusLabel}
          </span>
        ) : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{statusText}</span>
      </div>
    </div>
  );
}
